import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, StateToken, Store} from '@ngxs/store';
import {Observable, of, throwError} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {LoginAction} from '../actions/auth.actions';
import {AccountApiService} from "../api/account-api.service";
import {JobPollingService} from "../api/job-polling.service";
import {
  GetAccountPersonalDataAction,
  GetAccountStatusAction,
  RecoverPasswordAction,
  RequestInvestorContractDocumentAction,
  RequestInvestorProfileAction,
  ResetPasswordAction,
  SignupAction,
  UpdateIdentityVerificationStatus,
  UpdateKycInformacionAdicionalFisicaAction,
  UpdateKycInformacionAdicionalJuridicaAction,
  VerifyFisicaInformationAction,
  VerifyInformationAction
} from '../actions/account.actions';
import {SignupRequest} from "../models/signup-request.model";
import {Navigate} from "@ngxs/router-plugin";
import {CompanyData} from "../models/company-data.model";
import {AccountStatus, AccountUserType} from "../models/account-status.model";
import {FeatureFlags, resolveFeatureFlags} from "../models/feature-flags.model";
import {AccountPersonalData} from "../models/account-personal-data.model";
import {SendToast} from "../actions/toast.actions";
import {ToastType} from "../actions/core.actions";
import {FisicaVerficacionDatosModel} from '../models/fisica-verficacion-datos.model';
import {UserDocumentTypeListRequest} from '../models/user-document-type-list-request';

export interface AccountStateModel {
  signupError: boolean | null
  signupErrorMessage: string | null
  recoverPasswordError:boolean | null
  recoverPasswordErrorMessage:string | null
  resetPasswordError:boolean | null
  resetPasswordErrorMessage:string | null
  profileImage:string | null
  accountStatus:AccountStatus | null
  accountPersonalData:AccountPersonalData | null
  performingSignup: boolean | null
  verificationResult: any | null
}

const initialState: AccountStateModel = {
  signupError: false,
  signupErrorMessage: null,
  recoverPasswordError:false,
  recoverPasswordErrorMessage:null,
  resetPasswordError:false,
  resetPasswordErrorMessage:null,
  profileImage:null,
  accountStatus:null,
  accountPersonalData:null,
  performingSignup: false,
  verificationResult: null
};

@State<AccountStateModel>({
  name: new StateToken<AccountStateModel>('account'),
  defaults: initialState
})
@Injectable()
export class AccountState {

  constructor(
    private store:Store,
    private accountApiService: AccountApiService,
    private jobPollingService: JobPollingService,
  ) {}

  @Action(SignupAction)
  handleSignupAction(ctx: StateContext<AccountStateModel>, action: SignupAction) {

    const signupRequest:SignupRequest = action.payload;

    ctx.patchState({
      signupError: false,
      resetPasswordError:false,
      performingSignup: true
    })

    return this.accountApiService.signup(signupRequest).pipe(
      switchMap(signupResponse => {
        const state = ctx.getState();
        ctx.patchState({
          signupError: false,
          signupErrorMessage: null
        })
        return ctx.dispatch(new LoginAction({user:signupRequest.email, pass:signupRequest.password, returnUrl:"/thank-you", remember:false})).pipe(
          switchMap(loginResponse=>{
          return ctx.dispatch(new GetAccountStatusAction()).pipe(tap(() => { ctx.patchState({ performingSignup: false })}))
        }));
      }),
      catchError(err => {
        if(err && err.error) {
          const message  = err.error;
          console.log('ERROR', err);
          let msg = "";
          if (message.includes("Alias") && message.includes("Email")) {
            msg ='El e-mail y el usuario seleccionados ya se encuentran en uso. Elige otros para continuar.'
            // msg = 'El Usuario y EL E-mail que elegiste ya se encuentran en uso. Elige otros para continuar. \n El E-mail suministrado ya se encuentra en uso. Elige otro para continuar. '
          } else if(message.indexOf("Alias")>=0) {
            msg = 'El Usuario que elegiste ya se encuentra en uso. Elige otro para continuar.'
          } else if(message.indexOf("Email")>=0) {
            msg = 'El E-mail suministrado ya se encuentra en uso. Elige otro para continuar.'
          } else {
           msg = 'Hubo un problema al crear la cuenta. Intenta más tarde.'
          }
          ctx.patchState({
            signupError: true,
            signupErrorMessage: msg,
          })
        }
        return throwError(err)
      })
    );
  }


  @Action(RecoverPasswordAction)
  handleRecoverPasswordAction(ctx: StateContext<AccountStateModel>, action: RecoverPasswordAction) {

    const email:string = action.payload.email;

    return this.accountApiService.recoverPassword(email).pipe(
      switchMap(recoverPasswordResponseRaw => {
        if(recoverPasswordResponseRaw.status == 204) {
          ctx.patchState({
            recoverPasswordError: true,
            recoverPasswordErrorMessage: 'No hay infomación asociada al correo electrónico provisto.'
          })
        } else if(recoverPasswordResponseRaw.status == 409) {
          ctx.patchState({
            recoverPasswordError: true,
            recoverPasswordErrorMessage: 'Ocurrió un problema al recuperar la password. Intente nuevamente más tarde.'
          })
        } else {
          let recoverPasswordResponse = recoverPasswordResponseRaw.body;
          ctx.patchState({
            recoverPasswordError: false,
            recoverPasswordErrorMessage: null
          })
        }
        return of({});
      }),
      catchError(err => {
        if(err && err.error) {
          const message = err.error=='This should be application specific'?'Ocurrió un error. Intente nuevamente más tarde.':err.error;
          ctx.patchState({
            recoverPasswordError: true,
            recoverPasswordErrorMessage: message,
          })
        }
        return throwError(err)
      })
    );
  }

  @Action(ResetPasswordAction)
  handleResetPasswordAction(ctx: StateContext<AccountStateModel>, action: ResetPasswordAction) {

    const { code, token, password } = action.payload;

    return this.accountApiService.resetPassword(code, token, password).pipe(
      tap(changePasswordResponse => {
        const state = ctx.getState();

        ctx.patchState({
          resetPasswordError:false,
          resetPasswordErrorMessage:null
        });
      }),
      catchError(err => {
          ctx.patchState({
            resetPasswordErrorMessage:err.error,
            resetPasswordError:true
          });

        return throwError(err)
      })
    );
  }

  @Action(UpdateIdentityVerificationStatus)
  handleUpdateIdentityVerificationStatus(ctx: StateContext<AccountStateModel>, action: UpdateIdentityVerificationStatus) {

    let identityDetail = action.payload;

    return this.accountApiService.updateVerificationId(identityDetail).pipe(
      switchMap(changePasswordResponse => {
        const state = ctx.getState();

        // ctx.patchState({
        //   needsVerificationId:false,
        //   updateVerificationIdError:false,
        //   updateVerificationIdErrorMessage:null
        // });

        return ctx.dispatch(new GetAccountStatusAction())
      }),
      catchError(err => {
        // ctx.patchState({
        //   needsVerificationId:true,
        //   updateVerificationIdError:true,
        //   updateVerificationIdErrorMessage:err.error
        // });

        return throwError(err)
      })
    );
  }

  @Action(VerifyInformationAction)
  handleVerifyInformationAction(ctx: StateContext<AccountStateModel>, action: VerifyInformationAction) {
    const companyData: any = action.payload.userDocumentTypeList;

    return this.accountApiService.updateVerificationCompanyData(companyData).pipe(
      switchMap((response) => {
        if (!response?.jobId) return of(response);
        return this.jobPollingService.pollJob(response.jobId);
      }
      ),
      tap(result => {
        console.log('resultado', result);
        ctx.patchState({
          verificationResult: result
        });

        return this.store.dispatch([new SendToast({
          title: 'Correcto',
          content: 'Verificación Iniciada',
          style: ToastType.Success,
          timeout: 8000,
          closeable: false,
          progress: false,
          timestamp: new Date().getTime()
        })]);
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }
  @Action(VerifyFisicaInformationAction)
  handleVerifyFisicaInformationAction(ctx: StateContext<AccountStateModel>, action: VerifyFisicaInformationAction): Observable<any> {
    console.log('VERIFY FISICA');
    const documentLevel1List: UserDocumentTypeListRequest = action.payload.userDocumentTypeList;

    return this.accountApiService.updateFisicaVerificationData(documentLevel1List).pipe(
      tap(updateVerificationCompanyDataResponse => {
        const state = ctx.getState();
        return this.store.dispatch([new SendToast({
          title: 'Correcto',
          content: 'Verificación Iniciada',
          style: ToastType.Success,
          timeout: 8000,
          closeable: false,
          progress: false,
          timestamp: new Date().getTime()
        })]);
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }
  @Action(GetAccountStatusAction)
  handleGetAccountStatusAction(ctx: StateContext<AccountStateModel>, action: GetAccountStatusAction) {

    return this.accountApiService.getAccountStatus().pipe(
      tap(getAccountStatusResponse => {
        const state = ctx.getState();
        ctx.patchState({
          accountStatus:getAccountStatusResponse
        })
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }

  @Action(GetAccountPersonalDataAction)
  handleGetAccountPersonalDataAction(ctx: StateContext<AccountStateModel>, action: GetAccountPersonalDataAction) {

    return this.accountApiService.getAccountPersonalData().pipe(
      tap(getAccountPersonalDataResponse => {
        const state = ctx.getState();
        ctx.patchState({
          accountPersonalData:{...getAccountPersonalDataResponse}
        })
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }

  @Action(UpdateKycInformacionAdicionalFisicaAction)
  handleUpdateKycInformacionAdicionalFisicaAction(ctx: StateContext<AccountStateModel>, action: UpdateKycInformacionAdicionalFisicaAction) {
    let info = action.payload;
    return this.accountApiService.updateKycInformacionAdicionalFisicaAction(info).pipe(
      switchMap(updateKycInformacionAdicionalFisicaActionResponse => {
        return ctx.dispatch(new GetAccountStatusAction())
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }

  @Action(UpdateKycInformacionAdicionalJuridicaAction)
  handleUpdateKycInformacionAdicionalJuridicaAction(ctx: StateContext<AccountStateModel>, action: UpdateKycInformacionAdicionalJuridicaAction) {

    let info = action.payload;

    return this.accountApiService.updateKycInformacionAdicionalJuridicaAction(info).pipe(
      switchMap(updateKycInformacionAdicionalJuridicaResponse => {
        return ctx.dispatch(new GetAccountStatusAction())
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }

  @Action(RequestInvestorProfileAction)
  handleRequestInvestorProfileAction(ctx: StateContext<AccountStateModel>, action: RequestInvestorProfileAction) {
    return this.accountApiService.requestInvestorProfile().pipe(
      switchMap(() => {
        return ctx.dispatch(new GetAccountStatusAction())
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }
  
  @Action(RequestInvestorContractDocumentAction)
  handleRequestInvestorContractDocumentAction(ctx: StateContext<AccountStateModel>, action: RequestInvestorContractDocumentAction) {
    return this.accountApiService.getInvestorState().pipe(
      switchMap(() => {
        return ctx.dispatch(new GetAccountStatusAction())
      }),
      catchError(err => {
        return throwError(err)
      })
    );
  }

  @Selector()
  static getRecoverPasswordStatus(state: AccountStateModel) {
    return {
      recoverPasswordError:state?.recoverPasswordError,
      recoverPasswordErrorMessage:state?.recoverPasswordErrorMessage
    }
  }

  @Selector()
  static getResetPasswordStatus(state: AccountStateModel) {
    return {
      resetPasswordError:state?.resetPasswordError,
      resetPasswordErrorMessage:state?.resetPasswordErrorMessage
    }
  }

  @Selector()
  static getAccountStatus(state: AccountStateModel):AccountStatus {
    return state?.accountStatus
  }

  @Selector()
  static getFeatureFlags(state: AccountStateModel): FeatureFlags {
    return resolveFeatureFlags(state?.accountStatus?.featureFlags);
  }

  @Selector()
  static isEcheqLoadByIdEnabled(state: AccountStateModel): boolean {
    return resolveFeatureFlags(state?.accountStatus?.featureFlags).echeqLoadById;
  }

  @Selector()
  static getPuedeVer(state: AccountStateModel):AccountStatus {
    return state?.accountStatus
  }

  @Selector()
  static getAccountPersonalData(state: AccountStateModel) {
    return state?.accountPersonalData
  }

  // @Selector()
  // static getVerificationIdStatus(state: AccountStateModel) {
  //   return {
  //     needVerificationId:state.needsVerificationId,
  //     updateVerificationIdError:state.updateVerificationIdError,
  //     updateVerificationIdErrorMessage:state.updateVerificationIdErrorMessage
  //   }
  // }

  @Selector()
  static getSignupStatus(state: AccountStateModel) {
    return {
      signupError:state?.signupError,
      signupErrorMessage:state?.signupErrorMessage
    }
  }
  @Selector()
  static getPerformingSignup(state: AccountStateModel):boolean {
    return state?.performingSignup
  }
  @Selector()
  static getUserType(state: AccountStateModel): AccountUserType {
    return state?.accountStatus?.userType;
  }

  @Selector()
  static getVerificationResult(state: AccountStateModel) {
    return state?.verificationResult
  }
}
